import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { fetchConsentForms } from "../../store/slices/consentSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const ConsentList = ({ onAddConsent, onEditConsent, onDeleteConsent }) => {
    const dispatch = useDispatch();
    const { consentForms, isLoading, error } = useSelector((state) => state.consent);

    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch consent forms on component mount
    useEffect(() => {
        dispatch(fetchConsentForms());
    }, [dispatch]);

    // Filtered consents
    const filteredConsents = (consentForms || []).filter((c) =>
        Object.values(c || {}).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredConsents.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // Custom Pagination
    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // Columns
    // Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        {
            name: "Client Name",
            selector: (row) => row.client_name || "",
            sortable: true,
        },
        {
            name: "Primary Caregiver Name",
            selector: (row) => row.primary_name || "",
            sortable: true,
        },
        {
            name: "Primary Caregiver Cell",
            selector: (row) => row.primary_cell || "",
            sortable: true,
        },
        {
            name: "Primary Caregiver Relation",
            selector: (row) => row.primary_relation || "",
            sortable: true,
        },
        {
            name: "Created By",
            selector: (row) => row.creator?.name || "", // Access related user name
            sortable: true,
        },
        {
            name: "Updated By",
            selector: (row) => row.updater?.name || "", // Access related user name
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditConsent && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Consent"
                            onClick={() => onEditConsent(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteConsent && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Consent"
                            onClick={() => onDeleteConsent(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "140px",
        },
    ];


    // Export to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Consents List", 14, 10);
        const tableColumn = [
            "S.No",
            "Consent Title",
            "Client Name",
            "Consent Type",
            "Date Signed",
            "Created By",
            "Updated By",
            "Updated Date",
        ];
        const tableRows = [];

        filteredConsents.forEach((c, index) => {
            tableRows.push([
                index + 1,
                c.title || "",
                c.client_name || "",
                c.consent_type || "",
                c.date_signed || "",
                c.created_by || "",
                c.updated_by || "",
                c.updated_at || "",
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save("consents_list.pdf");
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredConsents}
                                    filename="consents_export.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search consent..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} consents
                </h6>

                {/* Right: Add Button */}
                <div>
                    {onAddConsent && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add Consent"
                            onClick={onAddConsent}
                        >
                            <i className="ri-add-line me-1"></i>
                            Add Consent
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredConsents}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={isLoading}
                    noDataComponent={
                        <div className="p-3 text-muted">
                            {error ? error : "No consents found"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default ConsentList;
